import * as THREE from 'three';
import { animate } from 'animejs';
import { createRenderer, createScene } from './engine/renderer.js';
import { createCamera, CameraRig } from './engine/camera.js';
import { createDualTonePostprocess } from './engine/postprocess.js';
import { getLightingQuality } from './engine/lightingQuality.js';
import { Input } from './engine/input.js';
import { AudioSystem } from './engine/audio.js';
import { AssetLoader, prepareModel } from './engine/loader.js';
import {
  bumpModelVersionInPageUrl,
  resolveModelVersionToken,
} from './engine/modelHotReload.js';
import { Game } from './game/game.js';
import { HUD } from './game/hud.js';
import { MenuController } from './game/ui/menuCtl.js';
import { runBlackoutTransition, runLoreIntroTransition } from './game/ui/animator.js';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

const CANVAS = document.getElementById('game');
const WALL_THICKNESS_SCALE_PARAM = 'wallThickness';
const PERF_STATS_PARAM = 'perfStats';
const PERF_PARAM = 'perf';

function resolveWallThicknessScale(search = window.location.search) {
  const raw = new URLSearchParams(search).get(WALL_THICKNESS_SCALE_PARAM);
  if (raw == null || raw === '') return 1;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return 1;
  // Keep bounds permissive for quick art tuning while preventing degenerate values.
  return Math.max(0.1, Math.min(2, parsed));
}

function shouldLogPerfStats(search = window.location.search) {
  const params = new URLSearchParams(search);
  const raw = params.get(PERF_STATS_PARAM) ?? params.get(PERF_PARAM);
  if (raw == null) return false;
  const normalized = String(raw).trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'on';
}

function bindUiHoverSfx(audio) {
  let hoveredEl = null;
  const selector = 'button, .frag-card, .character-card';

  document.addEventListener('pointerover', (ev) => {
    const target = ev.target;
    if (!(target instanceof Element)) return;
    const el = target.closest(selector);
    if (!el) return;
    if (el === hoveredEl) return;
    hoveredEl = el;
    audio.play('uiHover', { pitchJitter: 0.02 });
  });

  document.addEventListener('pointerout', (ev) => {
    if (!hoveredEl) return;
    const related = ev.relatedTarget;
    if (related instanceof Node && hoveredEl.contains(related)) return;
    hoveredEl = null;
  });
}

function bindUiClickSfx(audio) {
  const roots = [
    document.getElementById('menu'),
    document.getElementById('character-select'),
    document.getElementById('pause'),
  ].filter(Boolean);
  if (roots.length === 0) return;

  const onClick = (ev) => {
    const target = ev.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('button');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    audio.play('uiClick', { pitchJitter: 0.01 });
  };

  for (const root of roots) root.addEventListener('click', onClick);
}

function bindMainMenuButtonHoverFx() {
  const menuRoot = document.getElementById('menu');
  const pauseRoot = document.getElementById('pause');
  const buttons = [
    ...(menuRoot?.querySelectorAll('.menu-actions button') || []),
    ...(pauseRoot?.querySelectorAll('button') || []),
  ];
  if (!buttons.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const activeTweens = new WeakMap();

  const stopTween = (el) => {
    const tween = activeTweens.get(el);
    if (tween) tween.cancel();
  };

  const animateIn = (el) => {
    stopTween(el);
    const tween = animate(el, {
      scale: 1.045,
      translateY: -3,
      duration: 95,
      ease: 'outCubic',
    });
    activeTweens.set(el, tween);
  };

  const animateOut = (el) => {
    stopTween(el);
    const tween = animate(el, {
      scale: 1,
      translateY: 0,
      duration: 120,
      ease: 'outQuad',
    });
    activeTweens.set(el, tween);
  };

  buttons.forEach((btn) => {
    btn.addEventListener('pointerenter', () => animateIn(btn));
    btn.addEventListener('pointerleave', () => animateOut(btn));
    btn.addEventListener('focus', () => animateIn(btn));
    btn.addEventListener('blur', () => animateOut(btn));
  });
}

function bindAudioControls(audio) {
  const defaults = { master: 70, sfx: 80, music: 50 };
  const storageKeys = {
    master: 'nihil_vol_master',
    sfx: 'nihil_vol_sfx',
    music: 'nihil_vol_music',
  };
  let settings = { ...defaults };

  for (const key of Object.keys(storageKeys)) {
    const raw = Number(localStorage.getItem(storageKeys[key]));
    settings[key] = Number.isFinite(raw) ? raw : defaults[key];
  }

  const apply = () => {
    audio.setMasterVolume(settings.master / 100);
    audio.setSfxVolume(settings.sfx / 100);
    audio.setMusicVolume(settings.music / 100);
  };
  apply();

  const syncControlValues = (kind, value) => {
    document.querySelectorAll(`[data-audio-control="${kind}"]`).forEach((el) => {
      if (el.value !== String(value)) el.value = String(value);
    });
  };

  const controls = document.querySelectorAll('[data-audio-control]');
  controls.forEach((input) => {
    const kind = input.getAttribute('data-audio-control');
    if (!kind || !(kind in settings)) return;
    input.value = String(settings[kind]);
    input.addEventListener('input', () => {
      settings[kind] = Math.max(0, Math.min(100, Number(input.value) || 0));
      syncControlValues(kind, settings[kind]);
      apply();
      try { localStorage.setItem(storageKeys[kind], String(settings[kind])); } catch (_e) {}
    });
  });
}

function bindMenuSettingsPanel(menuRoot) {
  if (!menuRoot) return;
  const toggleBtn = menuRoot.querySelector('#btn-menu-settings-toggle');
  const panel = menuRoot.querySelector('#menu-settings-panel');
  if (!toggleBtn || !panel) return;

  const setExpanded = (expanded) => {
    toggleBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    panel.hidden = !expanded;
    menuRoot.classList.toggle('settings-open', expanded);
  };

  setExpanded(false);
  toggleBtn.addEventListener('click', () => {
    const expanded = toggleBtn.getAttribute('aria-expanded') === 'true';
    setExpanded(!expanded);
  });
}

function animateButtonPressFeedback(buttonEl) {
  if (!buttonEl) return Promise.resolve();
  buttonEl.classList.add('is-pressed');
  return new Promise((resolve) => {
    window.setTimeout(() => {
      buttonEl.classList.remove('is-pressed');
      resolve();
    }, 95);
  });
}

function bindModelHotReload() {
  const reloadWithFreshModels = () => {
    window.location.assign(bumpModelVersionInPageUrl());
  };

  // Dev helper: press Ctrl+Shift+L to force fresh /assets/models/* fetch.
  window.addEventListener('keydown', (ev) => {
    if (ev.ctrlKey && ev.shiftKey && ev.code === 'KeyL') {
      ev.preventDefault();
      reloadWithFreshModels();
    }
  });

  // Handy from devtools or automated smoke checks.
  window.__reloadModels = reloadWithFreshModels;
}

async function boot() {
  // Jam-safe toggle: keep bosses procedural to reduce load cost and risk.
  const ENABLE_BOSS_FBX = false;
  const menuCtl = new MenuController();
  const menuRoot = document.getElementById('menu');
  const menuBlackout = document.getElementById('menu-blackout');
  const runLoreIntro = document.getElementById('run-lore-intro');
  const lightingQuality = getLightingQuality();
  const renderer = createRenderer(CANVAS);
  const labelRenderer = new CSS2DRenderer();
  labelRenderer.setSize(window.innerWidth, window.innerHeight);
  labelRenderer.domElement.style.position = 'absolute';
  labelRenderer.domElement.style.top = '0';
  labelRenderer.domElement.style.left = '0';
  labelRenderer.domElement.style.pointerEvents = 'none';
  document.getElementById('app')?.appendChild(labelRenderer.domElement);
  const perfStatsEnabled = shouldLogPerfStats();
  const { scene } = createScene();
  const camera = createCamera();
  const postprocess = createDualTonePostprocess(renderer, scene, camera, {
    enabled: true,
    pixelEnabled: true,
    dualToneEnabled: false,
    pixelSize: 2.5,
    normalEdgeStrength: 0.3,
    depthEdgeStrength: 0.4,
    shadowColor: 0x03121a,
    highlightColor: 0xe2385b,
    threshold: 0.3,
    softness: 0.26,
    contrast: .9,
    saturationMix: 0.9,
    bloomEnabled: lightingQuality.cinematic.bloom.enabled,
    bloomStrength: lightingQuality.cinematic.bloom.strength,
    bloomRadius: lightingQuality.cinematic.bloom.radius,
    bloomThreshold: lightingQuality.cinematic.bloom.threshold,
  });
  // Handy for live tweaking from devtools without code edits.
  window.__dualTone = {
    get: () => postprocess.getOptions(),
    set: (next) => postprocess.setOptions(next),
    loadLutImage: (url) => postprocess.loadLutImage(url),
    loadLutCube: (url) => postprocess.loadLutCube(url),
    loadLut3dl: (url) => postprocess.loadLut3dl(url),
    clearLut: () => postprocess.clearLut(),
  };
  // ─── PATCH: main.js — ClaudeLightingPreset A/B helper ────────────────
  // Usage from devtools:
  //   __claudePreset.enable()   → cinematic HTML-match look
  //   __claudePreset.disable()  → previous values
  //   __claudePreset.toggle()
  const _origSceneState = {
    ambientIntensity: 0.04,
    hemiIntensity: 0.07,
    sunIntensity: 0.12,
    fogNear: 26,
    fogFar: 76,
    exposure: 1.2,
  };
  const _claudeState = {
    ambientIntensity: 0.04,
    hemiIntensity: 0.0,
    sunIntensity: 0.05,
    fogNear: 32,
    fogFar: 90,
    exposure: 1.2,
  };
  let _claudePresetActive = false;
  const _applyPreset = (s) => {
    scene.children.forEach((c) => {
      if (c.isAmbientLight) c.intensity = s.ambientIntensity;
      if (c.isHemisphereLight) c.intensity = s.hemiIntensity;
      if (c.isDirectionalLight && c.position.x > 0) c.intensity = s.sunIntensity;
    });
    scene.fog.near = s.fogNear;
    scene.fog.far = s.fogFar;
    renderer.toneMappingExposure = s.exposure;
  };
  window.__claudePreset = {
    enable() {
      _claudePresetActive = true;
      _applyPreset(_claudeState);
      console.log('[preset] Claude lighting ON');
    },
    disable() {
      _claudePresetActive = false;
      _applyPreset(_origSceneState);
      console.log('[preset] Claude lighting OFF');
    },
    toggle() { _claudePresetActive ? this.disable() : this.enable(); },
    isActive() { return _claudePresetActive; },
  };
  // Auto-enable on load so you start from the reference look.
  window.__claudePreset.enable();
  const cameraRig = new CameraRig(camera, { viewSize: 26, pitch: Math.PI / 3.2, distance: 40 });
  const input = new Input(CANVAS, camera);
  const audio = new AudioSystem();
  const modelVersionToken = resolveModelVersionToken();
  const assets = new AssetLoader({ modelVersionToken });

  window.addEventListener('resize', () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    postprocess.resize(window.innerWidth, window.innerHeight);
    labelRenderer.setSize(window.innerWidth, window.innerHeight);
    cameraRig.resize();
  });

  // Register audio (missing files fail silently; the game still works)
  audio.registerSfx('uiHover', ['/assets/audio/Retro3.wav'], { volume: 0.26, pool: 6 });
  audio.registerSfx('uiClick', ['/assets/audio/FUI Holographic Interaction Radiate.wav'], { volume: 0.8, pool: 8 });
  audio.registerSfx('titleGlitch', ['/assets/audio/Static Glitch Short.wav'], { volume: 0.34, pool: 6 });
  audio.registerSfx('footstep', ['/assets/audio/FootstepsConcrete3.ogg'], { volume: 0.75, pool: 10 });
  audio.registerSfx('shot', ['/assets/audio/DesignedGunshot_Pistol1_Reverb.ogg'], { volume: 0.32, pool: 8 });
  audio.registerSfx('launcherFire', ['/assets/audio/GUNArtl_Grenade Launcher Fire_05.wav'], { volume: 0.6, pool: 8 });
  audio.registerSfx('meleeShot', ['/assets/audio/swipe.wav'], { volume: 0.42, pool: 8 });
  audio.registerSfx('reloadStart', ['/assets/audio/DesignedGunSoundReload3.ogg'], { volume: 0.68, pool: 6 });
  audio.registerSfx('reloadEnd', ['/assets/audio/DesignedGunSoundReload3.ogg'], { volume: 0.6, pool: 6 });
  audio.registerSfx('emptyShot', ['/assets/audio/GUNMech_Empty Clip Burst B_01.wav'], { volume: 0.32, pool: 6 });
  audio.registerSfx('ammoPickup', ['/assets/audio/ammo_pickup.wav', '/assets/audio/ammo_pickup.mp3'], { volume: 0.4, pool: 6 });
  audio.registerSfx('itemPickup', ['/assets/audio/Coins_Placed_Down_Hardcover_Pitched.wav'], { volume: 0.36, pool: 8 });
  audio.registerSfx('enemyShot', ['/assets/audio/GUNTech_Sci%20Fi%20Shotgun%20Fire_04.wav'], { volume: 0.2, pool: 8 });
  audio.registerSfx('bossShot', ['/assets/audio/GUNCano_Quad%20Cannon%20Fire%20Double_01.wav'], { volume: 0.6, pool: 10 });
  audio.registerSfx('roomCleared', ['/assets/audio/DSGNStngr_Kill%20Confirm%20Metallic_02.wav'], { volume: 0.45, pool: 2 });
  audio.registerSfx('roomClearBonus', ['/assets/audio/room_clear.wav'], { volume: 0.52, pool: 2 });
  audio.registerSfx('fragmentPick', ['/assets/audio/weapon_upgrade.wav'], { volume: 0.45, pool: 3 });
  audio.registerSfx('cardFan', ['/assets/audio/card_fan.wav'], { volume: 0.34, pool: 3 });
  audio.registerSfx('cardHover', ['/assets/audio/card_draw_2.wav'], { volume: 0.22, pool: 8 });
  audio.registerSfx('hit', ['/assets/audio/hit.wav', '/assets/audio/hit.mp3'], { volume: 0.5 });
  audio.registerSfx('enemyHit', ['/assets/audio/Blade Metal Impact Recoil 01.wav'], { volume: 0.34, pool: 12 });
  audio.registerSfx('enemyDeath', ['/assets/audio/Gore Splat Stab Poke 03.wav'], { volume: 0.58, pool: 8 });
  audio.registerSfx('enemyDeathPunch', ['/assets/audio/HIT_METAL_WRENCH_HEAVIEST_02.wav'], { volume: 0.28, pool: 8 });
  audio.registerSfx('playerTerminated', ['/assets/audio/18_stinger_laser_shark_master.mp3'], { volume: 0.72, pool: 2 });
  audio.registerSfx('victoryStinger', ['/assets/audio/17_stinger_pink_door_master.mp3'], { volume: 0.75, pool: 2 });
  audio.registerSfx('dash', ['/assets/audio/Blade Swing Noise Slice 02.wav'], { volume: 0.42, pool: 10 });
  audio.registerSfx('doorOpen', ['/assets/audio/door.wav', '/assets/audio/door.mp3'], { volume: 0.6 });
  audio.registerSfx('explode', ['/assets/audio/explode.wav', '/assets/audio/explode.mp3'], { volume: 0.7 });
  audio.registerSfx('launcherExplode', ['/assets/audio/EXPLDsgn_Implode_15.wav'], { volume: 0.5, pool: 8 });
  audio.registerSfx('playerLevelStart', ['/assets/audio/PlayerVoice.wav'], { volume: 0.55, pool: 3 });
  audio.registerMusic('menu', [encodeURI('/assets/audio/1_the_first_cyborg_master.mp3')]);
  audio.registerMusic('combat', [encodeURI('/assets/audio/2_purple_city_master.mp3')]);
  audio.registerMusic('boss', [encodeURI('/assets/audio/LVS04_03_BattleAmidstTheStorm_loop.wav')]);
  bindUiHoverSfx(audio);
  bindUiClickSfx(audio);
  menuCtl.setAudio(audio);
  bindMainMenuButtonHoverFx();
  bindAudioControls(audio);
  bindMenuSettingsPanel(menuRoot);
  bindModelHotReload();

  // Asset paths (loaded in background after menu is visible)
  const baseModelPath = '/assets/models/';
  const playerModelPath = `${baseModelPath}Player/NewAnimation/`;
  const boss01Path = `${baseModelPath}Boss01/`;
  const boss02Path = `${baseModelPath}Boss02/`;

  const collectAnimations = (fbx) => {
    if (!fbx) return [];
    const clips = [];
    if (Array.isArray(fbx.animations)) clips.push(...fbx.animations);
    fbx.traverse?.((node) => {
      if (Array.isArray(node.animations) && node.animations.length) {
        clips.push(...node.animations);
      }
    });
    return clips;
  };
  const pickFirstLoadedFbx = (candidates) => candidates.find(Boolean) || null;
  const normalizeTrackName = (trackName) => {
    if (!trackName) return trackName;
    const dotIdx = trackName.indexOf('.');
    if (dotIdx <= 0) return trackName;
    const target = trackName.slice(0, dotIdx);
    const prop = trackName.slice(dotIdx);
    const normalizedTarget = target
      .replace(/bones\[([^\]]+)\]/g, (_m, boneName) => `bones[${boneName.split('|').pop()}]`)
      .split('/')
      .map((seg) => seg.split('|').pop())
      .join('/');
    return `${normalizedTarget}${prop}`;
  };
  const namedClips = (fbx, fallbackName) =>
    collectAnimations(fbx).map((clip, i) => {
      const c = clip.clone();
      c.name = `${fallbackName}_${clip.name?.trim() || i}`;
      c.tracks = clip.tracks
        .map((track) => {
          const t = track.clone();
          t.name = normalizeTrackName(t.name);
          return t;
        });
      return c;
    });

  const hud = new HUD();
  const wallThicknessScale = resolveWallThicknessScale();
  const game = new Game({
    scene,
    cameraRig,
    audio,
    input,
    assets,
    hud,
    menuCtl,
    postprocess,
    wallThicknessScale,
  });
  game._perf = {
    sections: {
      enemiesUpdate: { ms: 0, calls: 0 },
      projectilesUpdate: { ms: 0, calls: 0 },
      pathfind: { ms: 0, calls: 0 },
      spawn: { ms: 0, calls: 0 },
      outline: { ms: 0, calls: 0 },
      minimap: { ms: 0, calls: 0 },
      render: { ms: 0, calls: 0 },
    },
  };
  menuCtl.setGame(game);
  window.__enemyDropPreview = {
    atPlayer: () => game._triggerEnemyDropPreviewAtPlayer?.(),
    clear: () => game._clearEnemyDropPreview?.(),
  };
  // Dev tweak: append ?wallThickness=0.7 (or other value) to tune thickness quickly.
  window.__wallThicknessScale = wallThicknessScale;
  // Start with empty assets so gameplay works immediately with procedural fallbacks.
  game.setAssets({
    playerFbx: null,
    boss01Fbx: null,
    boss02Fbx: null,
    kitbashFbx: null,
  });

  // ---- Background asset orchestrator (menu never blocks on these) ----
  const yieldToFrame = () => new Promise((r) => setTimeout(r, 0));

  const loadPlayerAssets = async () => {
    menuCtl.setLoadingState('Loading character');
    const [
      playerIdleFbx,
      playerRunForwardFbx,
      playerRunBackwardFbx,
      playerRunLeftFbx,
      playerRunRightFbx,
      playerShootFbx,
    ] = await Promise.all([
      assets.loadFBX(`${playerModelPath}Idle.fbx`).catch(() => null),
      assets.loadFBX(`${playerModelPath}Run Forward.fbx`).catch(() => null),
      assets.loadFBX(`${playerModelPath}Run Backward.fbx`).catch(() => null),
      assets.loadFBX(`${playerModelPath}Run Left.fbx`).catch(() => null),
      assets.loadFBX(`${playerModelPath}Run Right.fbx`).catch(() => null),
      assets.loadFBX(`${playerModelPath}Shooting.fbx`).catch(() => null),
    ]);
    const playerFbx = playerIdleFbx || null;
    if (playerFbx) {
      playerFbx.animations = [
        ...namedClips(playerIdleFbx, 'idle'),
        ...namedClips(playerRunForwardFbx, 'run_forward'),
        ...namedClips(playerRunBackwardFbx, 'run_backward'),
        ...namedClips(playerRunLeftFbx, 'run_left'),
        ...namedClips(playerRunRightFbx, 'run_right'),
        ...namedClips(playerShootFbx, 'shoot'),
      ];
      prepareModel(playerFbx);
    }
    game.setAssets({ playerFbx });
  };

  const loadBoss01Assets = async () => {
    if (!ENABLE_BOSS_FBX) {
      game.setAssets({ boss01Fbx: null });
      return null;
    }
    menuCtl.setLoadingState('Loading mini-boss');
    const [b01Idle, b01Walk, b01Aim, b01Jump, b01Slash] = await Promise.all([
      assets.loadFBX(`${boss01Path}Orc Idle.fbx`).catch(() => null),
      assets.loadFBX(`${boss01Path}Orc Walk.fbx`).catch(() => null),
      assets.loadFBX(`${boss01Path}Standing Aim Overdraw.fbx`).catch(() => null),
      assets.loadFBX(`${boss01Path}Mutant Jump Attack.fbx`).catch(() => null),
      assets.loadFBX(`${boss01Path}Great Sword Slash.fbx`).catch(() => null),
    ]);
    const boss01Fbx = pickFirstLoadedFbx([b01Idle, b01Walk, b01Aim, b01Jump, b01Slash]);
    if (boss01Fbx) {
      boss01Fbx.animations = [
        ...namedClips(b01Idle, 'idle'),
        ...namedClips(b01Walk, 'walk'),
        ...namedClips(b01Aim, 'aim'),
        ...namedClips(b01Jump, 'jump'),
        ...namedClips(b01Slash, 'slash'),
      ];
      prepareModel(boss01Fbx);
    } else {
      console.warn('[main] Boss01 FBX missing, using procedural boss fallback.');
    }
    game.setAssets({ boss01Fbx });
    // Stash walk/idle/jump clips for boss02 animation sharing (same rig family).
    bossSharedClips.idle = b01Idle;
    bossSharedClips.walk = b01Walk;
    bossSharedClips.jump = b01Jump;
    return boss01Fbx;
  };

  const bossSharedClips = { idle: null, walk: null, jump: null };

  const loadBoss02Assets = async () => {
    if (!ENABLE_BOSS_FBX) {
      game.setAssets({ boss02Fbx: null });
      return null;
    }
    menuCtl.setLoadingState('Loading final boss');
    const [b02Charge, b02PowerUp, b02Slash] = await Promise.all([
      assets.loadFBX(`${boss02Path}Charge.fbx`).catch(() => null),
      assets.loadFBX(`${boss02Path}Sword And Shield Power Up.fbx`).catch(() => null),
      assets.loadFBX(`${boss02Path}Sword And Shield Slash.fbx`).catch(() => null),
    ]);
    const boss02Fbx = pickFirstLoadedFbx([b02Charge, b02PowerUp, b02Slash]);
    if (boss02Fbx) {
      boss02Fbx.animations = [
        ...namedClips(bossSharedClips.idle, 'idle'),
        ...namedClips(bossSharedClips.walk, 'walk'),
        ...namedClips(b02Charge, 'charge'),
        ...namedClips(b02PowerUp, 'powerup'),
        ...namedClips(b02Slash, 'slash'),
        ...namedClips(bossSharedClips.jump, 'jump'),
      ];
      prepareModel(boss02Fbx);
    } else {
      console.warn('[main] Boss02 FBX missing, using procedural boss fallback.');
    }
    game.setAssets({ boss02Fbx });
    return boss02Fbx;
  };

  // Singleton-promise wrappers so triggers can be called repeatedly cheaply.
  const once = (fn) => {
    let p = null;
    return () => (p ||= fn());
  };
  const assetOrchestrator = {
    ensurePlayer: once(loadPlayerAssets),
    ensureBoss01: once(loadBoss01Assets),
    ensureBoss02: once(loadBoss02Assets),
  };
  game.setAssetOrchestrator(assetOrchestrator);

  // Button bindings
  document.getElementById('btn-start').addEventListener('click', () => {
    menuCtl.beginCharacterSelect('run');
  });
  document.getElementById('btn-sandbox').addEventListener('click', () => {
    menuCtl.beginCharacterSelect('sandbox');
  });
  let startTransitionLocked = false;
  document.addEventListener('character-select:confirm', async (ev) => {
    if (startTransitionLocked) return;
    const payload = ev?.detail || {};
    game.setSelectedCharacter(payload.characterId);
    if (payload.mode === 'sandbox') {
      menuCtl.hideMenu();
      game.startSandbox();
      return;
    }
    startTransitionLocked = true;
    const startConfirmBtn = document.getElementById('btn-char-select-confirm');
    await animateButtonPressFeedback(startConfirmBtn);
    await runBlackoutTransition(menuBlackout, {
      fadeOutDuration: 0.3,
      holdDuration: 0.06,
      fadeInDuration: 0.3,
      onBlack: async () => {
        menuCtl.hideMenu();
        await runLoreIntroTransition(runLoreIntro, [
          'Year 2187. Vorrex.',
          "You don't exist anymore.",
          'Run.',
        ], {
          fadeInDuration: 0.14,
          lineDurations: [1.0, 1.15, 0.68],
          lineGap: 0.14,
          holdDuration: 0.86,
          fadeOutDuration: 0.34,
        });
        game.startRun();
      },
    });
    startTransitionLocked = false;
  });
  document.getElementById('btn-resume').addEventListener('click', () => game.resume());
  document.getElementById('btn-retry').addEventListener('click', () => { menuCtl.hideGameOver(); game.startRun(); });
  document.getElementById('btn-menu').addEventListener('click', () => { menuCtl.hideGameOver(); game.quitRun(); });
  document.getElementById('btn-victory-menu').addEventListener('click', () => { menuCtl.hideVictory(); game.quitRun(); });

  menuCtl.showMenu();
  audio.playMusic('menu');

  // Kick off staggered background loads after the menu is on-screen.
  // Each stage yields to the frame loop so the menu stays responsive.
  (async () => {
    await yieldToFrame();
    await assetOrchestrator.ensurePlayer();
    // Boss FBX are optional and disabled during jam stabilization.
    if (ENABLE_BOSS_FBX) {
      await yieldToFrame();
      await assetOrchestrator.ensureBoss01();
      await yieldToFrame();
      await assetOrchestrator.ensureBoss02();
    }
    menuCtl.setLoadingState(null);
  })().catch((err) => {
    console.warn('[main] background asset load failed:', err);
    menuCtl.setLoadingState(null);
  });

  // Main loop
  const clock = new THREE.Clock();
  let last = performance.now();
  let perfSampleElapsed = 0;
  let perfSampleFrames = 0;
  let perfSampleDtSum = 0;
  function frame(now) {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;

    input.setPaused(game.state === 'paused' || game.state === 'menu' || game.state === 'draft' || game.state === 'dying' || game.state === 'gameover' || game.state === 'victory');
    input.updateCursorWorld();
    hud.updateCrosshair(input.mouse.screenX, input.mouse.screenY);

    if (input.consumeEscape()) {
      if (game.state === 'playing') game.pause();
      else if (game.state === 'paused') game.resume();
    }

    game.update(dt);
    cameraRig.update(dt);
    hud.updateExitIndicator(game.room, cameraRig.camera);
    if (perfStatsEnabled) {
      perfSampleElapsed += dt;
      perfSampleFrames += 1;
      perfSampleDtSum += dt;
      if (perfSampleElapsed >= 2) {
        const avgMs = (perfSampleDtSum / Math.max(1, perfSampleFrames)) * 1000;
        const sections = game._perf?.sections || {};
        const sectionAvg = Object.fromEntries(
          Object.entries(sections).map(([key, value]) => {
            const avg = (value?.ms || 0) / Math.max(1, perfSampleFrames);
            return [key, Number(avg.toFixed(3))];
          })
        );
        console.debug('[perf]', {
          avgFrameMs: Number(avgMs.toFixed(2)),
          fps: Number((1000 / Math.max(0.001, avgMs)).toFixed(1)),
          sectionsAvgMs: sectionAvg,
          renderCalls: renderer.info.render.calls,
          triangles: renderer.info.render.triangles,
          points: renderer.info.render.points,
          lines: renderer.info.render.lines,
        });
        perfSampleElapsed = 0;
        perfSampleFrames = 0;
        perfSampleDtSum = 0;
        for (const section of Object.values(sections)) {
          section.ms = 0;
          section.calls = 0;
        }
      }
    }

    const renderStart = performance.now();
    postprocess.render(dt);
    labelRenderer.render(scene, cameraRig.camera);
    const renderMs = performance.now() - renderStart;
    if (game._perf?.sections?.render) {
      game._perf.sections.render.ms += renderMs;
      game._perf.sections.render.calls += 1;
    }
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

boot().catch((err) => {
  console.error(err);
});
